import React, { useEffect, useState } from 'react';
import { Box, AppBar, Toolbar, Typography, Container, Button, Alert, CircularProgress } from '@mui/material';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { prefetchSmsCascadeContext } from '../../api/smsCascadePrefetch';
import { prefetchSubscriberContactLists } from '../../api/subscribersApi';
import ThemeSelector from '../common/ThemeSelector';
import LanguageSwitcher from '../common/LanguageSwitcher';
import SmsCabinetBackButton from '../common/SmsCabinetBackButton';
import SmsTopUpBalanceButton from '../common/SmsTopUpBalanceButton';
import SmsHeaderBalance from '../common/SmsHeaderBalance';
import { SMS_PAGE, useSmsPageDesign } from '../../theme/smsPageDesign';
import { useTranslation } from 'react-i18next';
import SmsSiteHeader from '../common/SmsSiteHeader';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { apiGet } from '../../api/client';

type AuthCheckState = 'checking' | 'ok' | 'redirecting' | 'error';

const MainLayout: React.FC = () => {
  const { t } = useTranslation();
  const { isSmsLight } = useSmsPageDesign();
  const [authState, setAuthState] = useState<AuthCheckState>('checking');
  const [authError, setAuthError] = useState<string | null>(null);
  const location = useLocation();
  const isEditor = /^\/mailing\//.test(location.pathname);
  const isCampaignHome = location.pathname === '/';
  const showAppBar = !isEditor && !isCampaignHome;

  useEffect(() => {
    let cancelled = false;
    setAuthState('checking');
    setAuthError(null);
    void apiGet<{ userId: number }>('/api/auth/me')
      .then(() => {
        if (!cancelled) setAuthState('ok');
      })
      .catch((e: Error & { status?: number }) => {
        if (cancelled) return;
        if (e.status === 401) {
          setAuthState('redirecting');
          return;
        }
        if (e.status === 503) {
          setAuthError(t('auth.serverAuthDisabledHint'));
          setAuthState('error');
          return;
        }
        setAuthError(e.message || t('auth.checkFailed'));
        setAuthState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (authState !== 'ok') return;
    void prefetchSmsCascadeContext();
    void prefetchSubscriberContactLists();
  }, [authState]);

  const authGate = authState !== 'ok' ? (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: isSmsLight ? SMS_PAGE.bg : 'background.default', px: 2 }}>
      <Box sx={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        {authState === 'error' ? (
          <Alert severity="warning" sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('auth.serverAuthDisabled')}
            </Typography>
            <Typography variant="body2">{authError || t('auth.checkFailed')}</Typography>
          </Alert>
        ) : (
          <>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {authState === 'redirecting' ? t('auth.redirecting') : t('auth.checking')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  ) : null;

  if (authGate) return authGate;

  return (
    <Box
      sx={{
        minHeight: isEditor ? undefined : '100vh',
        height: isEditor ? '100vh' : undefined,
        overflow: isEditor ? 'hidden' : undefined,
        display: isEditor ? 'flex' : undefined,
        flexDirection: isEditor ? 'column' : undefined,
        bgcolor: isSmsLight ? SMS_PAGE.bg : 'background.default',
      }}
    >
      {isCampaignHome ? <SmsSiteHeader /> : null}
      {showAppBar && (
        <AppBar
          component="header"
          position="static"
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 58,
            '& .MuiToolbar-root': { minHeight: 58, py: 0 },
          }}
        >
          <Toolbar disableGutters sx={{ px: 2, gap: 1, flexWrap: 'wrap' }}>
            <SmsCabinetBackButton />
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              color="inherit"
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textDecoration: 'none',
              }}
            >
              <AccountTreeOutlinedIcon sx={{ fontSize: { xs: 20, sm: 24 }, flexShrink: 0 }} />
              {t('layout.campaigns')}
            </Typography>
            <Button color="inherit" component={RouterLink} to={{ pathname: '/', search: '?tab=scenarios' }} size="small">
              {t('layout.chains')}
            </Button>
            <SmsHeaderBalance sx={{ ml: { xs: 0, sm: 0.5 } }} />
            <SmsTopUpBalanceButton size="small" color="inherit" variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <LanguageSwitcher inverted />
            <ThemeSelector inverted />
          </Toolbar>
        </AppBar>
      )}
      <Container
        maxWidth={isEditor ? false : 'xl'}
        disableGutters={isEditor}
        sx={{
          py: isEditor ? 0 : { xs: 2.5, sm: 3 },
          flex: isEditor ? 1 : undefined,
          minHeight: isEditor ? 0 : undefined,
          overflow: isEditor ? 'hidden' : undefined,
          display: isEditor ? 'flex' : undefined,
          flexDirection: isEditor ? 'column' : undefined,
        }}
      >
        <Outlet />
      </Container>
    </Box>
  );
};

export default MainLayout;
